/* Dish photos. Two separate settings per photo (Dashboard → dish → Photos):
 *  - position: the Default photo (plate) shows on menu cards at rest, the Hover photo
 *    (takeaway box) fades in under the mouse — cardPhotos() below, whatever portion is picked;
 *  - kind ("Shows as" Main Dish / Meal Box): which photo the dish page, package builder,
 *    Meal Boxes page and emails pick for a portion or package — dishPhoto() and friends.
 * A dish can have a Main Dish photo and a Meal Box photo. Meal Sets and boxed-meal packages show the Meal Box one; dish-only portions and
 * main-dish / Small Table packages show the Main Dish one. A type with no photo borrows the
 * other, so a dish with a single photo shows it everywhere. Leave `kind` out for the photo
 * of the portion the dish opens on. */

// The API returns /media/... paths; media is only served from the API host (the SPA host
// answers those paths with index.html). Same rule the rest of the storefront follows.
export function mediaUrl(path) {
  if (!path) return '';
  return /^https?:\/\//.test(path) ? path : `https://api.chds.com.au${path}`;
}

export function photoKindForPortion(p) {
  if (!p) return undefined;
  return String(p.portion_code || '').toLowerCase() === 'medium' || /meal.?set/i.test(p.portion_name || '') ? 'box' : 'main';
}

export function photoKindForPackage(pkg) {
  if (!pkg) return undefined;
  return pkg.kind === 'boxed' ? 'box' : 'main';
}

/* The dish page and weekly cards open on the first listed portion; the classic menu popup
 * (and the cards that lead to it) on the lightest one. */
export function defaultPhotoKind(d, { lightestFirst = false } = {}) {
  let ps = ((d && d.portion) || []).filter((p) => p.price !== null);
  if (lightestFirst) ps = [...ps].sort((a, b) => (parseFloat(a.portion_weight) || 0) - (parseFloat(b.portion_weight) || 0));
  return photoKindForPortion(ps[0]) || 'main';
}

export function dishPhoto(d, kind) {
  const imgs = (d && d.item_images) || [];
  const k = kind || defaultPhotoKind(d);
  return imgs.find((x) => x.kind === k) || imgs.find((x) => x.kind) || imgs.find((x) => x.is_main) || imgs[0] || null;
}
export function dishImage(d, kind) {
  const i = dishPhoto(d, kind);
  return i ? mediaUrl(i.image) : '';
}
export function dishFocal(d, kind) {
  const i = dishPhoto(d, kind);
  return i && i.focal_x != null ? `${i.focal_x}% ${i.focal_y}%` : 'center';
}
export function dishFocalPoint(d, kind) {
  const i = dishPhoto(d, kind);
  return i && i.focal_x != null ? { x: i.focal_x, y: i.focal_y } : null;
}

// Hover only where there is a real mouse: phones and tablets keep the Default photo (and
// never download the Hover one).
export function canHover() {
  try { return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches); } catch (e) { return false; }
}

const focalOf = (i) => (i && i.focal_x != null ? { x: i.focal_x, y: i.focal_y } : null);

/* Menu cards: { src, focal, hoverSrc, hoverFocal }. Default = the photo set as Default (else the
 * Main Dish / main / first photo); Hover only when the dish has a second photo in that position. */
export function cardPhotos(d) {
  const imgs = (d && d.item_images) || [];
  const def = imgs.find((x) => x.position === 'default') || imgs.find((x) => x.kind === 'main') || imgs.find((x) => x.is_main) || imgs[0] || null;
  const hov = imgs.find((x) => x.position === 'hover' && (!def || x.id !== def.id)) || null;
  return { src: def ? mediaUrl(def.image) : '', focal: focalOf(def), hoverSrc: hov ? mediaUrl(hov.image) : '', hoverFocal: focalOf(hov) };
}
export function cardImage(d) { return cardPhotos(d).src; }
export function cardFocal(d) { const f = cardPhotos(d).focal; return f ? `${f.x}% ${f.y}%` : 'center'; }
export function cardHover(d) { const c = cardPhotos(d); return c.hoverSrc ? { src: c.hoverSrc, focal: c.hoverFocal } : null; }
